import React, { useState, FC, useMemo, memo } from 'react'

import { Tooltip as MuiTooltip, tooltipClasses, TooltipProps } from '@mui/material'
import { styled as MuiStyled } from '@mui/material/styles'
import clsx from 'clsx'
import styled from 'styled-components'

import { Icon } from '../icons/Icon'
import { Txt } from '../texts/Txt'

const StyledTooltip = MuiStyled(({ className, open, ...props }: TooltipProps) => (
  // 公式参考、childrenが必要とIDEで表示されるが問題なし
  // https://mui.com/components/tooltips/#customization
  <MuiTooltip
    {...props}
    classes={{ popper: className }}
    enterTouchDelay={0 /* モバイル端末でタップした際に表示する */}
    // dropdownの下に隠れてしまうためz-indexを設定
    PopperProps={{ style: { zIndex: 20_000 } }}
    open={open}
  />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#24262B',
  },
  [`&.roov-primary .${tooltipClasses.arrow}`]: {
    color: '#060E6D',
  },
  [`&.hover .${tooltipClasses.tooltip}`]: {
    filter: 'drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.4))',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#24262B',
    borderRadius: '4px',
    padding: '11px',
    maxWidth: 'none',
    fontFamily:
      'inherit' /* fontがMUIで定義されたものになってしまうためhtml,bodyで定義しているfontをinheritさせる */,
    fontSize: '11px',
    filter: 'drop-shadow(0px 5px 20px rgba(0, 0, 0, 0.3))',
    transition:
      'filter 0.1s, opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important' /* MUIのtransitionを上書きしつつ、filterの設定を追加する */,
  },
  [`&.roov-primary .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#060E6D',
    borderRadius: '13px',
    fontWeight: 'bold',
  },
}))

const DefaultTxt = styled(Txt)`
  max-width: 28rem;
  white-space: pre-wrap;
  line-height: var(--r1);
`
const FixedChildrenWrapper = styled.div`
  max-width: 21rem;
`

type TooltipWrapperProps = {
  children: React.ReactElement<any, any>
}

// eslint-disable-next-line react/display-name
const TooltipWrapper = React.forwardRef<HTMLDivElement, TooltipWrapperProps>((props, ref) => {
  return <span {...props} ref={ref} />
})

type TooltipColorType = 'white' | 'gray' | 'dark'
type TooltipTheme = 'dark' | 'roovPrimary'
type TooltipIconPlacement = 'left' | 'right'

type Props = {
  text?: string
  showIcon?: boolean
  content?: React.ReactNode
  colorType?: TooltipColorType
  placement?: TooltipProps['placement']
  iconPlacement?: TooltipIconPlacement
  theme?: TooltipTheme
  // NOTE: openを指定する事で外から表示/非表示のタイミングを切り替えられる。指定しない場合はhoverした場合のみ表示される
  open?: boolean
  // openがtrueの場合にshowCheckもtrueにすることでチェックマークを表示する
  showCheck?: boolean
  disabled?: boolean
  /** openがtrue かつ showCheckがtrue の場合に表示されるチェックマークをクリックした際に呼ばれる */
  onClickCheck?: () => void
} & TooltipWrapperProps

export const Tooltip: FC<Props> = memo(
  ({
    text,
    showIcon = true,
    content,
    colorType = 'dark',
    placement,
    iconPlacement = 'right',
    theme = 'dark',
    open,
    showCheck = false,
    disabled = false,
    onClickCheck,
    children,
  }) => {
    const [isHover, setIsHover] = useState(false)
    const [isCheckIconHover, setIsCheckIconHover] = useState(false)

    const iconColorType = useMemo(
      () =>
        colorType === 'white'
          ? isHover
            ? 'gray'
            : 'white'
          : colorType === 'gray'
          ? isHover
            ? 'white'
            : 'gray'
          : isHover
          ? 'black'
          : 'dark',
      [colorType, isHover]
    )

    if (disabled) {
      return children
    }

    // openを使って固定表示する際に利用するTooltipのタイトル用コンポーネント
    // チェックマークを押下する事で onClickCheck を呼び出す
    const FixedChildren: React.ReactNode = (
      <FixedChildrenWrapper key="fixedChildren" className="tw-flex tw-items-start">
        <DefaultTxt tag="div" sizeType="s">
          {text}
        </DefaultTxt>
        {showCheck && (
          <div
            role="presentation"
            className="tw-p-2 tw-cursor-pointer tw-ml-4"
            onClick={onClickCheck}
            onMouseEnter={() => setIsCheckIconHover(true)}
            onMouseLeave={() => setIsCheckIconHover(false)}
          >
            <Icon size="--r0" path="check/soft/line" />
          </div>
        )}
      </FixedChildrenWrapper>
    )

    return (
      <StyledTooltip
        title={
          open
            ? FixedChildren
            : content || (
                <DefaultTxt tag="div" sizeType="s">
                  {text}
                </DefaultTxt>
              )
        }
        arrow
        placement={placement}
        open={!disabled && open}
        className={`tooltip ${isCheckIconHover ? 'hover' : ''} ${
          theme === 'roovPrimary' ? 'roov-primary' : ''
        }`}
      >
        <span
          className={clsx(
            'tw-inline-flex tw-items-center tw-gap-s-3',
            iconPlacement === 'right' ? 'tw-flex-row' : 'tw-flex-row-reverse'
          )}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          {/* カスタムコンポーネントを利用する場合はrefを渡す必要がある */}
          <TooltipWrapper>{children}</TooltipWrapper>
          {showIcon && !disabled && (
            <Icon
              size="--s1"
              path="help-circle/soft/line"
              className={clsx(
                iconColorType === 'white'
                  ? 'tw-text-inlineText-primary'
                  : iconColorType === 'gray'
                  ? 'tw-text-standardText-disable'
                  : iconColorType === 'dark'
                  ? 'tw-text-standardText-secondary'
                  : 'tw-text-standardText-primary'
              )}
            />
          )}
        </span>
      </StyledTooltip>
    )
  }
)
